import { useDebugValue, useMemo, useReducer } from "react";
import produce, { Draft } from "immer";

type PayloadAction<P> = {
    type: string;
    payload: P;
};

type PayloadActionDispatch<P = void> = void extends P
    ? () => void
    : (payload: P) => void;

type ReducerWithoutPayload<S> = (state: S) => S;

type PayloadActionReducer<S, P = void> = (
    state: S,
    action: PayloadAction<P>
) => void;

type ReducerMap<State> = {
    [actionType: string]: PayloadActionReducer<Draft<State>, any>;
};

type DispatcherMap<Reducers extends ReducerMap<any>> = {
    [T in keyof Reducers]: Reducers[T] extends ReducerWithoutPayload<any>
        ? PayloadActionDispatch<void>
        : Reducers[T] extends PayloadActionReducer<any, infer P>
            ? PayloadActionDispatch<P>
            : never;
};

interface UseLocalSliceOptions<State, Reducers extends ReducerMap<State>> {
    initialState: State;
    reducers: Reducers;
    slice?: string;
}

/**
 * This hook manages the state the same way useReducer does but removes a lot of the boilerplate.
 * @param {UseLocalSliceOptions<State, Reducers>} options
 * @param {State} options.initialState The initial state.
 * @param {Reducers extends ReducerMap<State>} options.reducers The reducers that change the state.
 * @param {string} options.slice The name of the slice.
 * @returns [the state, the dispatch actions required to trigger a state change]
 */
function useLocalSlice<State, Reducers extends ReducerMap<State>>({ initialState, reducers, slice = "unnamed" }: UseLocalSliceOptions<State, Reducers>): [State, DispatcherMap<Reducers>] {
    useDebugValue(slice);

    function reducer(state: State, action: PayloadAction<any>): State {
        const newState = produce(state, draftState => {
            reducers[action.type](draftState, action);
            return draftState;
        }) as State;
        return newState;
    }

    const [state, dispatch] = useReducer(reducer, initialState);

    const actionTypes = Object.keys(reducers);

    const dispatchAction = useMemo(
        () => {
            const map: ReducerMap<State> = {};
            for (const type of actionTypes)
                map[type] = payload => dispatch({ type, payload });
            return map as DispatcherMap<Reducers>;
        },
        [actionTypes]
    );

    return [state, dispatchAction];
}

export {
    DispatcherMap,
    PayloadAction,
    PayloadActionDispatch,
    PayloadActionReducer,
    ReducerMap,
    ReducerWithoutPayload,
    useLocalSlice,
    UseLocalSliceOptions
};
